import { useState, useEffect, useCallback } from 'react';
import { SimpleDropdown } from './SimpleDropdown';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { TeamDropdown } from './TeamDropdown';
import teamService from '../../services/teamService';

const CONFERENCE_STATES = {
  'ACCAC': 'AZ',
  'ACCC': 'AL',
  'ACCC North': 'AL',
  'ACCC South': 'AL',
  'Arrowhead Conference': 'IL',
  'Bay Valley Conference': 'CA',
  'CCAC': '',
  'CUNYAC': 'NY',
  'Carolinas': 'NC, SC',
  'Central Valley Conference': 'CA',
  'Citrus': 'FL',
  'Coast Conference (North)': 'CA',
  'Coast Conference (South)': '',
  'EPAC': 'PA',
  'GCAA North': 'GA',
  'GCAA South': 'GA',
  'GRAC': 'IL, IN',
  'GSAC': 'NJ',
  'Golden Valley Conference': 'Northern CA',
  'ICCAC': 'IA, NE',
  'Inland Empire Conference': 'CA',
  'Jayhawk': 'KS',
  'Jayhawk Conference East': 'KS',
  'Jayhawk Conference West': 'KS',
  'LCCAC': 'LA',
  'MACCC': 'MS',
  'MCAC': 'MN, ND, WI',
  'MCCAA': 'MI, IN',
  'MCCAC': 'MO',
  'MD JUCO': 'MD',
  'MSAC': 'NY',
  'MWAC': 'IL',
  'Mid Hudson': 'NY',
  'Mid-Hudson': 'NY',
  'Mon Dak': 'MT, ND',
  'Mon-Dak': 'MT, ND',
  'N4C': 'IL, WI',
  'NCCAC': 'NC',
  'NJCAA Independent': '',
  'NTJCAC': 'TX',
  'OCAC': 'OK',
  'OCCAC': 'OH',
  'Orange Empire Conference': 'CA',
  'Pacific Coast Conference': 'CA',
  'Panhandle': 'FL',
  'RMAC': 'CO, NE, WY',
  'SWAC': 'UT, NV, ID, CO',
  'SWJCAC': 'TX, NM',
  'Skyway Conference': 'IL',
  'South Coast Conference': 'CA',
  'Sun Lakes': 'AZ',
  'TCCAA Central': 'TN',
  'TCCAA East': 'TN',
  'TCCAA West': 'TN',
  'VCCS': 'VA',
  'WJCAC': 'TX, NM',
  'WPCC': 'PA',
  'WSC': 'CA',
  'WTCAC': 'TN',
  'Western State Conference': 'CA',
};

const CONFERENCE_OPTIONS = [
  { label: 'All', value: '' },
  ...Object.entries(CONFERENCE_STATES).map(([name, states]) => ({
    label: states ? `${name} (${states})` : name,
    value: name,
  })),
];

const DIVISION_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'NJCAA DI', value: 'I' },
  { label: 'NJCAA DII', value: 'II' },
  { label: 'NJCAA DIII', value: 'III' },
  { label: '3C2A', value: '3C2A' },
];

const CLASSIFICATION_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'FR', value: 'FR' },
  { label: 'SO', value: 'SO' },
  { label: 'R-FR', value: 'R-FR' },
  { label: 'R-SO', value: 'R-SO' },
];

const REGION_STATES = {
  1: 'AZ, CA, NV',
  2: 'AR, OK',
  3: 'NY',
  4: 'IL, WI',
  5: 'NM, TX',
  6: 'KS',
  7: 'TN, KY',
  8: 'FL',
  9: 'CO, NE, WY',
  10: 'NC, SC, VA, WV',
  11: 'IA, NE',
  12: 'IN, MI, OH',
  13: 'MN, ND, MI, WI, MT',
  14: 'TX',
  15: 'NY',
  16: 'MO',
  17: 'GA',
  18: 'CO, ID, NV, OR, WA, UT',
  19: 'PA, DE, NJ',
  20: 'MD, PA, WV',
  21: 'CT, MA, RI',
  22: 'AL',
  23: 'MS, LA',
  24: 'IL',
};

const REGION_OPTIONS = [
  { label: 'All', value: '' },
  ...Array.from({ length: 24 }, (_, i) => {
    const n = i + 1;
    return {
      label: `Region ${n} (${REGION_STATES[n]})`,
      value: `${n}`,
    };
  }),
];

const STATE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Alabama', value: 'AL' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

const HANDEDNESS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Bats R', value: 'bats-R' },
  { label: 'Bats L', value: 'bats-L' },
  { label: 'Bats S', value: 'bats-S' },
  { label: 'Throws R', value: 'throws-R' },
  { label: 'Throws L', value: 'throws-L' },
];

const SPLITS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'vR', value: 'vR' },
  { label: 'vL', value: 'vL' },
];

const EMPTY_FILTERS = {
  playerName: '',
  teamId: '',
  position: '',
  classification: '',
  conference: '',
  division: '',
  region: '',
  state: '',
  handedness: '',
  splits: '',
};

export const PlayerFilterModal = ({ isOpen, onClose, onApply, initialFilters = {} }) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...initialFilters });
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Teams state for TeamDropdown
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsHasMore, setTeamsHasMore] = useState(false);
  const [teamsLoadingMore, setTeamsLoadingMore] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');

  // Sync initialFilters every time modal opens
  useEffect(() => {
    if (isOpen) {
      setFilters({ ...EMPTY_FILTERS, ...initialFilters });
      // If initialFilters has a teamId, set selected team display
      if (!initialFilters.teamId) setSelectedTeam(null);
    }
  }, [isOpen, JSON.stringify(initialFilters)]);

  // Fetch teams on open or search
  const fetchTeams = useCallback(async (search = '', page = 1, append = false) => {
    if (page === 1) setTeamsLoading(true);
    else setTeamsLoadingMore(true);

    try {
      const data = await teamService.getPlayerTeams({ page, limit: 20, teamName: search });
      const list = data.teams || data.data || [];
      const total = data.pagination?.totalPages || data.totalPages || 1;

      setTeams((prev) => append ? [...prev, ...list] : list);
      setTeamsPage(page);
      setTeamsHasMore(page < total);
    } catch (_) {
      // silent
    } finally {
      setTeamsLoading(false);
      setTeamsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchTeams('', 1, false);
  }, [isOpen]);

  const handleTeamSearch = (val) => {
    setTeamSearch(val);
    fetchTeams(val, 1, false);
  };

  const handleLoadMore = () => {
    fetchTeams(teamSearch, teamsPage + 1, true);
  };

  const set = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    set('teamId', team ? (team._id || team.id) : '');
  };

  const activeCount = Object.values(filters).filter(Boolean).length;

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    setSelectedTeam(null);
    onApply(cleared);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-jp-field border border-jp-line rounded-2xl shadow-2xl w-full max-w-md overflow-visible">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-jp-line">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#06b6d4">
              <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0 0 18.95 4H5.04a1 1 0 0 0-.79 1.61z"/>
            </svg>
            <h2 className="text-jp-ink font-semibold text-base">Filter Players</h2>
          </div>
          <button onClick={onClose} className="text-jp-muted hover:text-jp-ink transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="px-6 pt-3 pb-1 text-jp-muted text-xs">
          Results will automatically update as you adjust the filters below
        </p>

        {/* Fields */}
        <div className="px-6 py-4 grid grid-cols-2 gap-4">

          {/* Player Name */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Player Name</label>
            <input
              type="text"
              value={filters.playerName}
              onChange={(e) => set('playerName', e.target.value)}
              placeholder="Search player..."
              className="w-full px-3 py-2 text-sm rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-jp-accent"
            />
          </div>

          {/* Team Name — uses existing TeamDropdown */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Team Name</label>
            <div className="h-[38px]">
              <TeamDropdown
                selectedTeam={selectedTeam}
                onSelectTeam={handleSelectTeam}
                teams={teams}
                loading={teamsLoading}
                placeholder="All"
                variant="admin"
                onSearch={handleTeamSearch}
                onLoadMore={handleLoadMore}
                hasMore={teamsHasMore}
                loadingMore={teamsLoadingMore}
              />
            </div>
          </div>

          {/* Position */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Position</label>
            <MultiSelectDropdown
              selected={filters.position ? filters.position.split(',').filter(Boolean) : []}
              onChange={(arr) => set('position', arr.join(','))}
              variant="themed"
            />
          </div>

          {/* Class */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Class</label>
            <SimpleDropdown
              value={filters.classification}
              onChange={(v) => set('classification', v)}
              options={CLASSIFICATION_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* Conference */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Conference</label>
            <SimpleDropdown
              value={filters.conference}
              onChange={(v) => set('conference', v)}
              options={CONFERENCE_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* Division */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Division</label>
            <SimpleDropdown
              value={filters.division}
              onChange={(v) => set('division', v)}
              options={DIVISION_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* Region */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Region</label>
            <SimpleDropdown
              value={filters.region}
              onChange={(v) => set('region', v)}
              options={REGION_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* State */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">State</label>
            <SimpleDropdown
              value={filters.state}
              onChange={(v) => set('state', v)}
              options={STATE_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* Handedness */}
          <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">Handedness</label>
            <SimpleDropdown
              value={filters.handedness}
              onChange={(v) => set('handedness', v)}
              options={HANDEDNESS_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div>

          {/* R/L Splits */}
          {/* <div>
            <label className="block text-jp-muted text-xs font-medium mb-1.5">R/L Splits</label>
            <SimpleDropdown
              value={filters.splits}
              onChange={(v) => set('splits', v)}
              options={SPLITS_OPTIONS}
              placeholder="All"
              variant="themed"
            />
          </div> */}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 pb-5 pt-3 border-t border-jp-line">
          <button
            onClick={handleClear}
            className="px-5 py-2 rounded-lg border border-jp-line text-jp-muted hover:text-jp-ink hover:border-jp-accent/60 text-sm font-medium transition-colors"
          >
            Reset Filters
          </button>
          <button
            onClick={handleApply}
            className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-colors"
          >
            View Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerFilterModal;
